import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Button,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaRegCheckCircle } from "react-icons/fa";
import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { useSearchParams } from "react-router-dom";

/* ================= TYPES ================= */

type Notification = {
  _id: string;
  user: string;
  message: string;
  processType?: string;
  processName?: string;
  processId?: string;
  createdAt: string;
  seenBy?: string[];
};

type Pagination = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

type SetActivePage = React.Dispatch<React.SetStateAction<string>>;

/* ================= COMPONENT ================= */

export default function BadgeMenuComponent({
  setActivePage,
  setNotificationCount,
}: {
  setActivePage: SetActivePage;
  setNotificationCount: React.Dispatch<React.SetStateAction<number>>;
}): JSX.Element {
  const [badgeLoading, setBadgeLoading] = useState(false)
  const { workFlowData, accountDetails } = useContext(AppContext);
  const setSearchParams = useSearchParams()[1]

  const [data, setData] = useState<Notification[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [selectedItem, setSelectedItem] = useState<"all" | "read" | "unread">(
    "unread"
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  /* ================= THEME ================= */

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const unreadBg = useColorModeValue("teal.50", "gray.700");

  /* ================= API ================= */

  const fetchNotifications = useCallback(
    async (type: string, page = 1, append = false) => {
      try {
        page === 1 ? setLoading(true) : setLoadingMore(true);

        const res: any = await appRequest("notifications", "all", {
          type,
          page,
          limit: 10,
        });

        setData((prev) =>
          append ? [...prev, ...(res?.data || [])] : res?.data || []
        );

        setPagination(res?.pagination || {});
      } catch (err) {
        console.error("Error fetching notifications:", err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  /* ================= EFFECT ================= */

  useEffect(() => {
    if (Object.keys(workFlowData || {}).length > 0) {
      fetchNotifications("unread", 1);
    }
  }, [fetchNotifications, workFlowData]);

  /* ================= ACTIONS ================= */

  const handleMenuItemClick = async (item: "all" | "read" | "unread") => {
    setSelectedItem(item);
    await fetchNotifications(item, 1);
  };

  const handleAllRead = async () => {
    try {
      await appRequest("notifications", "allasread", 1);
      setData([]);
      setNotificationCount(0);
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await appRequest("notifications", "markread", { id });

      setData((prev) =>
        selectedItem === "unread"
          ? prev.filter((n) => n._id !== id)
          : prev.map((n) =>
              n._id === id
                ? {
                    ...n,
                    seenBy: Array.from(
                      new Set([...(n.seenBy || []), accountDetails?.userId])
                    ),
                  }
                : n
            )
      );

      setNotificationCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking read:", err);
    }
  };

  const handleNotificationAction = (item: Notification) => {
    handleMarkRead(item._id);

    if (item.processType === "quote") setActivePage("quotes");
    else if (item.processType === "lead") setActivePage("leads");
    else if (item.processType === "workflow") setActivePage("workflow");
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchNotifications(selectedItem, pagination.page + 1, true);
    }
  };



  const viewDocument = async (item: any) => {
  try
  {
  const documentId = item?.documentId;

  if (!documentId) return;

  // return
  setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    params.set("documentId", documentId);
    return params;
  })


  setBadgeLoading(true)

  setActivePage("approvals");

  setTimeout(() => {
    window.location.reload();
  }, 50)
 }
 catch(err : any)
 {
  setBadgeLoading(false)
 }
};


  /* ================= UI ================= */

  return (
    <Box
      w={{ base: "100%", md: "380px" }}
      bg={bg}
      borderRadius="xl"
      border="1px"
      borderColor={borderColor}
      overflow="hidden"
    >
      {/* ===== HEADER ===== */}
      <Flex
        align="center"
        justify="space-between"
        p={4}
        borderBottom="1px"
        borderColor={borderColor}
      >
        <Flex align="center" gap={3}>
          <Text fontSize="lg" fontWeight="semibold">
            Notifications
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="outline"
              rightIcon={<ChevronDownIcon />}
            >
              {selectedItem.toUpperCase()}
            </MenuButton>
            <MenuList fontSize="sm">
              {["all", "unread", "read"].map((item) => (
                <MenuItem
                  key={item}
                  onClick={() =>
                    handleMenuItemClick(item as "all" | "read" | "unread")
                  }
                >
                  {item.toUpperCase()}
                  {selectedItem === item && <CheckIcon ml={2} />}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        {selectedItem === "unread" && <Button
          size="xs"
          variant="ghost"
          colorScheme="teal"
          leftIcon={<FaRegCheckCircle />}
          onClick={handleAllRead}
        >
          Mark all
        </Button>}
      </Flex>

      {/* ===== LIST ===== */}
      <Box ref={containerRef} maxH="420px" overflowY="auto" p={3}>
        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner color="teal.500" />
          </Flex>
        ) : data.length ? (
          <>
            {data.map((item) => {
              const isRead = item.seenBy?.includes(accountDetails?.userId);

              return (
                <Box
                  key={item._id}
                  p={4}
                  mb={2}
                  borderRadius="lg"
                  border="1px"
                  borderColor={borderColor}
                  bg={isRead ? bg : unreadBg}
                  _hover={{ bg: hoverBg }}
                  position="relative"
                >
                  {!isRead && (
                    <Box
                      position="absolute"
                      top="10px"
                      right="10px"
                      w="8px"
                      h="8px"
                      bg="teal.400"
                      borderRadius="full"
                    />
                  )}

                  <Text fontSize="sm" fontWeight="medium">
                    {item.message}
                  </Text>

                  <Flex justify="space-between" align="center" mt={3}>
                    <Text fontSize="xs" color={textMuted}>
                      {new Date(item.createdAt).toLocaleString()}
                    </Text>

                    <Flex gap={2}>
                      {!isRead && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="teal"
                          onClick={() => handleMarkRead(item._id)}
                        >
                          Read
                        </Button>
                      )}

                      {item.processType && (
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          isLoading={badgeLoading}
                          onClick={() => viewDocument(item)}
                          display="none"
                        >
                          {`View Document`}
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </Box>
              );
            })}

            {pagination.page < pagination.totalPages && (
              <Flex justify="center" pt={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleLoadMore}
                  isLoading={loadingMore}
                >
                  Show more
                </Button>
              </Flex>
            )}
          </>
        ) : (
          <Flex direction="column" align="center" py={10} color={textMuted}>
            <Image
              src="/img/no-notifications.png"
              alt="No notifications"
              boxSize="100px"
              mb={3}
              opacity={0.7}
            />
            <Text fontWeight="medium">No notifications</Text>
            <Text fontSize="sm">You're all caught up 🎉</Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
